import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Check, ExternalLink } from "lucide-react";
import PrimaryButton from "../PrimaryButton/PrimaryButton";
import SecondaryButton from "../SecondaryButton/SecondaryButton";
import PlainTextButton from "../PlainTextButton/PlainTextButton";
import Title from "../Title/Title";
import { InputV2 } from "../InputV2/InputV2";
import FileUploadWrapper from "../FileUploadWrapper/FileUploadWrapper";
import CopyText from "../CopyText/CopyText";
import PriceField from "../PriceField/PriceField";
import OnboardingWizardLayout, { OnboardingLanguageSwitcher } from "../OnboardingFlow/OnboardingWizardLayout";
import { onboardingChoiceSurface } from "../OnboardingFlow/onboardingBrand";
import CollectionsAPI, { Collection, Item } from "../../api/CollectionsAPI";
import CataloguesAPI, { Catalog } from "../../api/CataloguesAPI";
import OnboardingAPI from "../../api/OnboardingAPI";
import { toastService } from "../../services/ToastService";
import { useImageUploader } from "../../hooks/useImageUploader";
import { simplifyUrl } from "../../shared/urlUtils";
import CONFIG from "../../config/config";
import { ONBOARDING_PLAN_PATH } from "../../utils/onboardingFirstWin";
import { exampleCatalogPillLabel, ONBOARDING_EXAMPLE_CATALOGUE_UUIDS } from "../../utils/onboardingExampleCatalogs";

type DraftItem = {
  name: string;
  price: number;
  currency: string;
  file: File | null;
};

type ExampleCatalog = {
  label: string;
  uuid: string;
};

type CatalogAudience = "show_prices" | "hide_prices" | "private";

const AUDIENCE_OPTIONS: CatalogAudience[] = ["show_prices", "hide_prices", "private"];

const audienceFlags = (
  audience: CatalogAudience
): Pick<Collection, "public" | "shareableByLink" | "pricesArePublic" | "acceptsB2BOrders"> => {
  if (audience === "show_prices") {
    return { public: true, shareableByLink: true, pricesArePublic: true, acceptsB2BOrders: true };
  }
  if (audience === "hide_prices") {
    return { public: false, shareableByLink: true, pricesArePublic: false, acceptsB2BOrders: false };
  }
  return { public: false, shareableByLink: false, pricesArePublic: false, acceptsB2BOrders: false };
};

const emptyItem = (): DraftItem => ({ name: "", price: 0, currency: "", file: null });

const OnboardingCatalogFlow: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from || "/";
  const settings = useSelector((state: any) => state.settings.value);
  const brandName = settings?.tenantName || "";
  const { uploadImage } = useImageUploader("", (resp: any) => resp.data, "catalogues");

  const [stepIndex, setStepIndex] = useState(0);
  const [fade, setFade] = useState(true);
  const [loading, setLoading] = useState(false);
  const [catalogName, setCatalogName] = useState(brandName);
  const [items, setItems] = useState<DraftItem[]>([emptyItem(), emptyItem()]);
  const [audience, setAudience] = useState<CatalogAudience | undefined>();
  const [catalogLink, setCatalogLink] = useState("");
  const [exampleCatalogs, setExampleCatalogs] = useState<ExampleCatalog[]>([]);

  const isWow = stepIndex === 4;
  const isShareable = audience !== "private";

  useEffect(() => {
    let cancelled = false;
    CataloguesAPI.getCatalogues()
      .then((response) => {
        if (cancelled) {
          return;
        }
        const byUuid = new Map(
          (response.data || [])
            .filter((catalog: Catalog) => !!catalog.uuid)
            .map((catalog: Catalog) => [catalog.uuid as string, catalog])
        );
        const examples = ONBOARDING_EXAMPLE_CATALOGUE_UUIDS.flatMap((uuid) => {
          const catalog = byUuid.get(uuid);
          return catalog?.uuid
            ? [{ label: exampleCatalogPillLabel(catalog), uuid: catalog.uuid }]
            : [];
        });
        setExampleCatalogs(examples);
      })
      .catch((error) => {
        console.error("Failed to load example catalogues", { error });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const goToPlan = () => {
    navigate(ONBOARDING_PLAN_PATH, { state: { from: redirectTo, firstWin: "catalog" } });
  };

  const namedItems = items.filter((item) => item.name.trim().length > 2);
  const canProceed = () => {
    if (stepIndex === 1) return catalogName.trim().length > 2;
    if (stepIndex === 2) return namedItems.length >= 1;
    if (stepIndex === 3) return !!audience;
    return true;
  };

  const advance = () => {
    setFade(false);
    setTimeout(() => {
      setStepIndex((prev) => prev + 1);
      setFade(true);
    }, 300);
  };

  const goBack = () => {
    if (stepIndex === 0) return;
    setFade(false);
    setTimeout(() => {
      setStepIndex((prev) => prev - 1);
      setFade(true);
    }, 300);
  };

  const updateItem = (index: number, patch: Partial<DraftItem>) => {
    setItems((prev) => prev.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  };

  const removeItem = (index: number) => {
    setItems((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));
  };

  const addImageToItem = async (collectionId: number, item: Item, file: File): Promise<Item> => {
    const uploaded = await uploadImage(file, `collections/${collectionId}/${item.id}/files/images`, { media: item.media });
    if (typeof uploaded === "string") {
      return { ...item, media: [...(item.media || []), simplifyUrl(uploaded)] };
    }
    return uploaded;
  };

  const handleComplete = async () => {
    if (!audience) {
      return;
    }
    setLoading(true);
    try {
      const collectionPayload: Collection = {
        name: catalogName.trim(),
        shortName: catalogName.trim(),
        type: CONFIG.DEFAULT_PRODUCT_TYPE,
        types: [CONFIG.DEFAULT_PRODUCT_TYPE],
        featured: false,
        lastUpdateTime: null,
        items: [],
        views: 0,
        description: "",
        ...audienceFlags(audience),
      };
      const created = (await CollectionsAPI.add(collectionPayload)).data;
      const collectionId = created.id;
      if (!collectionId) {
        throw new Error("Created catalogue is missing an id");
      }

      for (const draft of namedItems.slice(0, 3)) {
        const priceValue = draft.price;
        const itemPayload: Item = {
          name: draft.name.trim(),
          featuredPictureUrl: "",
          type: CONFIG.DEFAULT_PRODUCT_TYPE,
          placeOrder: 0,
          ...(Number.isFinite(priceValue) && priceValue > 0
            ? { price: priceValue, ...(draft.currency ? { currency: draft.currency } : {}) }
            : {}),
        };
        let newItem = (await CollectionsAPI.addItem(collectionId, itemPayload)).data;
        const itemId = newItem.id;
        if (draft.file && itemId) {
          try {
            newItem = await addImageToItem(collectionId, newItem, draft.file);
            await CollectionsAPI.updateItem(collectionId, itemId, newItem);
          } catch (error) {
            console.error("Failed to add catalogue item image", { collectionId, error });
          }
        }
      }

      try {
        await OnboardingAPI.updateStep("publishCatalog");
      } catch (stepError) {
        console.error("Failed to mark publishCatalog onboarding step", { error: stepError });
      }

      const uuid = created.uuid;
      setCatalogLink(`${window.location.origin}/catalogues/${uuid || collectionId}`);
      advance();
    } catch (error) {
      console.error("Failed to create onboarding catalogue", { error });
      toastService.showToast(t("onboarding.catalog.error") as string, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (!canProceed()) return;
    if (stepIndex === 3) {
      handleComplete();
      return;
    }
    advance();
  };

  const wowDescriptionKey =
    audience === "private"
      ? "onboarding.catalog.final.private_description"
      : audience === "hide_prices"
        ? "onboarding.catalog.final.hide_prices_description"
        : "onboarding.catalog.final.description";

  const renderStep = () => {
    switch (stepIndex) {
      case 0:
        return (
          <div className="flex flex-col items-center space-y-8">
            <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl text-center">
              {t("onboarding.catalog.intro.description")}
            </p>
            {exampleCatalogs.length > 0 && (
              <div className="flex flex-col items-center gap-2">
                <p className="text-sm text-muted-foreground">{t("onboarding.catalog.intro.examples")}</p>
                <div className="flex flex-wrap justify-center gap-2">
                  {exampleCatalogs.map((catalog) => (
                    <a
                      key={catalog.uuid}
                      href={`${window.location.origin}/catalogues/${catalog.uuid}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted text-foreground text-sm px-3 py-1 hover:bg-muted/80"
                    >
                      <span className="truncate max-w-[22rem]">{catalog.label}</span>
                      <ExternalLink className="h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden="true" />
                    </a>
                  ))}
                </div>
              </div>
            )}
            <PrimaryButton tone="brand"
              text={t("onboarding.catalog.intro.button") as string}
              fn={handleNext}
              actionName="onboarding_catalog_intro"
            />
            <PlainTextButton tone="brand"
              text={t("onboarding.catalog.skip") as string}
              onClick={goToPlan}
              actionName="onboarding_catalog_skip"
            />
            <OnboardingLanguageSwitcher />
          </div>
        );
      case 1:
        return (
          <div className="flex flex-col items-center space-y-8">
            <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl text-center">
              {t("onboarding.catalog.step1.question")}
            </p>
            <div className="w-full max-w-md">
              <InputV2
                value={catalogName}
                onChange={(e) => setCatalogName(e.target.value)}
                placeholder={t("onboarding.catalog.step1.placeholder") as string}
              />
            </div>
            <div className="flex flex-col items-center gap-4 w-full max-w-md">
              <div className="flex flex-col sm:flex-row gap-4 w-full justify-center">
                <SecondaryButton text={t("onboarding.catalog.back") as string} fn={goBack} actionName="onboarding_catalog_back" />
                <PrimaryButton tone="brand"
                  text={t("onboarding.catalog.continue") as string}
                  fn={handleNext}
                  actionName="onboarding_catalog_next"
                  isDisabled={!canProceed()}
                />
              </div>
              <PlainTextButton tone="brand" text={t("onboarding.catalog.skip") as string} onClick={goToPlan} actionName="onboarding_catalog_skip" />
            </div>
            <OnboardingLanguageSwitcher />
          </div>
        );
      case 2:
        return (
          <div className="flex flex-col items-center space-y-8">
            <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl text-center">
              {t("onboarding.catalog.step2.question")}
            </p>
            <div className="w-full max-w-2xl space-y-4">
              {items.map((item, index) => (
                <div key={index} className="rounded-xl border border-border p-4 space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm font-medium text-foreground">
                      {t("onboarding.catalog.step2.item_label", { number: index + 1 })}
                    </p>
                    {items.length > 1 && (
                      <PlainTextButton tone="brand"
                        text={t("onboarding.catalog.step2.remove_item") as string}
                        onClick={() => removeItem(index)}
                        actionName="onboarding_catalog_remove_item"
                      />
                    )}
                  </div>
                  <InputV2
                    value={item.name}
                    onChange={(e) => updateItem(index, { name: e.target.value })}
                    placeholder={t("onboarding.catalog.step2.name_placeholder") as string}
                  />
                  <PriceField
                    label={"admin.collections.items.form.price"}
                    currency={item.currency}
                    price={item.price}
                    currencies={[""].concat(CONFIG.SUPPORTED_CURRENCIES)}
                    onCurrencyChange={(currency) => updateItem(index, { currency })}
                    onPriceChange={(price) => updateItem(index, { price, currency: item.currency })}
                  />
                  <FileUploadWrapper
                    label="onboarding.catalog.step2.photo"
                    allowedTypes={CONFIG.IMAGE_TYPES}
                    onChange={(file: File) => updateItem(index, { file })}
                  />
                  {item.file && (
                    <p className="text-xs text-muted-foreground">{item.file.name}</p>
                  )}
                </div>
              ))}
              {items.length < 3 && (
                <PlainTextButton tone="brand"
                  text={t("onboarding.catalog.step2.add_item") as string}
                  onClick={() => setItems((prev) => [...prev, emptyItem()])}
                  actionName="onboarding_catalog_add_item"
                />
              )}
            </div>
            <div className="flex flex-col items-center gap-4 w-full max-w-md">
              <div className="flex flex-col sm:flex-row gap-4 w-full justify-center">
                <SecondaryButton text={t("onboarding.catalog.back") as string} fn={goBack} actionName="onboarding_catalog_back" />
                <PrimaryButton tone="brand"
                  text={t("onboarding.catalog.continue") as string}
                  fn={handleNext}
                  actionName="onboarding_catalog_items_next"
                  isDisabled={!canProceed()}
                />
              </div>
              <PlainTextButton tone="brand" text={t("onboarding.catalog.skip") as string} onClick={goToPlan} actionName="onboarding_catalog_skip" />
            </div>
            <OnboardingLanguageSwitcher />
          </div>
        );
      case 3:
        return (
          <div className="flex flex-col items-center space-y-8">
            <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl text-center">
              {t("onboarding.catalog.step3.question")}
            </p>
            <div className="w-full max-w-2xl grid grid-cols-1 gap-3">
              {AUDIENCE_OPTIONS.map((option) => {
                const isSelected = audience === option;
                return (
                  <button
                    type="button"
                    key={option}
                    onClick={() => setAudience(option)}
                    className={`text-left p-4 rounded-xl border-2 transition-all duration-200 ${onboardingChoiceSurface(isSelected)}`}
                  >
                    <span className="text-foreground font-medium">
                      {t(`onboarding.catalog.step3.${option}`)}
                    </span>
                  </button>
                );
              })}
            </div>
            <div className="flex flex-col items-center gap-4 w-full max-w-md">
              <div className="flex flex-col sm:flex-row gap-4 w-full justify-center">
                <SecondaryButton text={t("onboarding.catalog.back") as string} fn={goBack} actionName="onboarding_catalog_back" />
                <PrimaryButton tone="brand"
                  text={t("onboarding.catalog.step2.button") as string}
                  fn={handleNext}
                  actionName="onboarding_catalog_complete"
                  isDisabled={!canProceed()}
                />
              </div>
              <PlainTextButton tone="brand" text={t("onboarding.catalog.skip") as string} onClick={goToPlan} actionName="onboarding_catalog_skip" />
            </div>
            <OnboardingLanguageSwitcher />
          </div>
        );
      case 4:
        return (
          <div className="flex flex-col items-center space-y-8">
            <div className="text-center space-y-4">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <Title
                text={t("onboarding.catalog.final.title") as string}
                clazz="text-3xl font-bold !text-foreground mb-4 !text-center"
              />
              <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl">
                {t(wowDescriptionKey)}
              </p>
            </div>
            {isShareable && (
              <div className="w-full max-w-2xl bg-muted rounded-lg p-6 space-y-4">
                <div className="flex flex-wrap items-center gap-2 bg-card p-3 rounded-md border border-border">
                  <span className="text-muted-foreground truncate flex-1 min-w-0">{catalogLink}</span>
                  <CopyText text={catalogLink} label={t("onboarding.catalog.final.copy_link") as string} />
                </div>
                <div className="space-y-3 pt-4 border-t border-border">
                  <p className="font-medium text-foreground">{t("onboarding.catalog.final.next_steps.title")}</p>
                  <p className="text-sm text-muted-foreground">{t("onboarding.catalog.final.next_steps.share")}</p>
                  <p className="text-sm text-muted-foreground">{t("onboarding.catalog.final.next_steps.instagram")}</p>
                </div>
              </div>
            )}
            <PrimaryButton tone="brand"
              text={t("onboarding.wow.continue") as string}
              fn={goToPlan}
              actionName="onboarding_catalog_finish"
            />
            <OnboardingLanguageSwitcher />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <OnboardingWizardLayout
      fade={fade}
      loading={loading}
      loadingText={t("onboarding.catalog.loading") as string}
      title={!isWow && stepIndex > 0 ? (t("onboarding.catalog.title") as string) : undefined}
      showProgress={!isWow && stepIndex > 0}
      progressTotal={3}
      progressIndex={stepIndex - 1}
    >
      {renderStep()}
    </OnboardingWizardLayout>
  );
};

export default OnboardingCatalogFlow;
